import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import SectionCard from '../../components/SectionCard';

const PATH_ISSUE = {
  OURS_BOTH: 'Issue service report',
  THEIR_INSPECT_OUR_REPAIR: 'Issue service report',
  EXTERNAL_BOTH: 'File the request record',
};

function readFile(file) {
  return new Promise((resolve, reject) => {
    if (file.size > 4 * 1024 * 1024) {
      reject(new Error('Keep each file under 4 MB'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read that file'));
    if (file.type.startsWith('image/') || file.type === 'application/pdf') reader.readAsDataURL(file);
    else reader.readAsText(file);
  });
}

function RepairCase() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState({ BEFORE: '', AFTER: '', REQUEST: '' });
  const [message, setMessage] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['repair-case', caseId],
    queryFn: async () => (await api.get(`/repair-cases/${caseId}`)).data.data,
  });

  const form = draft || data;

  const save = useMutation({
    mutationFn: async (body) => (await api.patch(`/repair-cases/${caseId}`, body)).data.data,
    onSuccess: (row) => {
      setDraft(null);
      queryClient.setQueryData(['repair-case', caseId], row);
      setMessage('Saved');
    },
  });

  const addItem = useMutation({
    mutationFn: async (body) => (await api.post(`/repair-cases/${caseId}/items`, body)).data.data,
    onSuccess: (row) => queryClient.setQueryData(['repair-case', caseId], row),
  });

  const removeItem = useMutation({
    mutationFn: async (itemId) => (await api.delete(`/repair-cases/${caseId}/items/${itemId}`)).data.data,
    onSuccess: (row) => queryClient.setQueryData(['repair-case', caseId], row),
  });

  const issue = useMutation({
    mutationFn: async (path) => {
      const url = path === 'reinspection' ? 'reinspection-passed' : 'service-report';
      return (await api.post(`/repair-cases/${caseId}/${url}`)).data.data;
    },
    onSuccess: (row) => {
      queryClient.setQueryData(['repair-case', caseId], row);
      queryClient.invalidateQueries({ queryKey: ['repair-cases'] });
      setMessage('Filed in the building Service report folder');
    },
  });

  if (isLoading || !form) return <div className="py-12 text-center">Loading...</div>;

  const setField = (key, value) => setDraft({ ...(draft || data), [key]: value });
  const items = (slot) => (data?.items || []).filter((item) => item.slot === slot);

  const addNote = async (slot) => {
    const body = note[slot].trim();
    if (!body) return;
    await addItem.mutateAsync({ slot, body });
    setNote((prev) => ({ ...prev, [slot]: '' }));
  };

  const addFiles = async (slot, fileList) => {
    setMessage('');
    for (const file of fileList) {
      try {
        const body = await readFile(file);
        await addItem.mutateAsync({ slot, body, original_name: file.name, caption: file.name });
      } catch (err) {
        setMessage(err.message || 'Could not add that file');
      }
    }
  };

  const slotCard = (slot, title, hint) => (
    <SectionCard title={title}>
      <p className="text-sm text-gray-700">{hint}</p>
      <textarea
        className="min-h-24 w-full rounded border border-green-300 px-3 py-2 text-sm"
        value={note[slot]}
        onChange={(e) => setNote((prev) => ({ ...prev, [slot]: e.target.value }))}
        placeholder="Paste an email, a report extract, or a work note"
      />
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" onClick={() => addNote(slot)}>
          Add note
        </button>
        <label className="cursor-pointer text-sm font-medium text-gray-900 underline">
          Add photo or file
          <input
            type="file"
            accept="image/*,.pdf,.txt,.eml"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(slot, [...(e.target.files || [])]);
              e.target.value = '';
            }}
          />
        </label>
      </div>
      <ul className="space-y-2">
        {items(slot).map((item) => (
          <li key={item.id} className="flex items-start justify-between gap-3 text-sm">
            <span>
              {String(item.body || '').startsWith('data:image/') ? (
                <img src={item.body} alt={item.caption || ''} className="mb-1 max-h-40 rounded" />
              ) : null}
              {String(item.body || '').startsWith('data:') ? <span className="font-semibold text-red-700">(after)</span> : null}
              <span className="block">{item.caption || item.original_name || item.body?.slice(0, 160)}</span>
              {item.note ? <span className="block">{item.note}</span> : null}
            </span>
            <button type="button" className="text-gray-600 underline" onClick={() => removeItem.mutate(item.id)}>
              Remove
            </button>
          </li>
        ))}
      </ul>
    </SectionCard>
  );

  const showService = form.path_key !== 'OUR_INSPECT_THEIR_REPAIR';
  const showReinspection = form.path_key === 'OURS_BOTH' || form.path_key === 'OUR_INSPECT_THEIR_REPAIR';

  return (
    <div>
      <button type="button" className="mb-3 text-sm text-gray-700 underline" onClick={() => navigate('/repairing')}>
        Back to Repairing
      </button>
      <SectionCard
        title={form.guide?.title || 'Repair case'}
        actions={
          <button
            type="button"
            className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white"
            onClick={() =>
              save.mutate({
                ss_code: form.ss_code,
                inspector_party: form.inspector_party,
                repairer_party: form.repairer_party,
                external_inspector: form.external_inspector,
                external_repairer: form.external_repairer,
                before_text: form.before_text,
                work_note: form.work_note,
              })
            }
          >
            Save
          </button>
        }
      >
        <p className="text-sm text-gray-800">{form.building_name}</p>
        <p className="text-sm text-gray-700">{form.guide?.collect}</p>
        <p className="text-sm text-gray-700">{form.guide?.issue}</p>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm">
            SS
            <input className="mt-1 w-full rounded border border-green-300 px-3 py-2" value={form.ss_code || ''} onChange={(e) => setField('ss_code', e.target.value)} />
          </label>
          <label className="text-sm">
            Inspected by
            <select className="mt-1 w-full rounded border border-green-300 px-3 py-2" value={form.inspector_party} onChange={(e) => setField('inspector_party', e.target.value)}>
              <option value="OURS">Our company</option>
              <option value="EXTERNAL">Another company</option>
            </select>
          </label>
          <label className="text-sm">
            Repaired by
            <select className="mt-1 w-full rounded border border-green-300 px-3 py-2" value={form.repairer_party} onChange={(e) => setField('repairer_party', e.target.value)}>
              <option value="OURS">Our company</option>
              <option value="EXTERNAL">Another company</option>
            </select>
          </label>
          {form.inspector_party === 'EXTERNAL' && (
            <label className="text-sm">
              Other inspector
              <input className="mt-1 w-full rounded border border-green-300 px-3 py-2" value={form.external_inspector || ''} onChange={(e) => setField('external_inspector', e.target.value)} />
            </label>
          )}
          {form.repairer_party === 'EXTERNAL' && (
            <label className="text-sm">
              Other repairer
              <input className="mt-1 w-full rounded border border-green-300 px-3 py-2" value={form.external_repairer || ''} onChange={(e) => setField('external_repairer', e.target.value)} />
            </label>
          )}
        </div>
        <label className="block text-sm">
          Before — their report or email
          <textarea className="mt-1 min-h-28 w-full rounded border border-green-300 px-3 py-2" value={form.before_text || ''} onChange={(e) => setField('before_text', e.target.value)} />
        </label>
        <label className="block text-sm">
          Work note
          <textarea className="mt-1 min-h-20 w-full rounded border border-green-300 px-3 py-2" value={form.work_note || ''} onChange={(e) => setField('work_note', e.target.value)} />
        </label>
      </SectionCard>

      {slotCard('BEFORE', 'Before files', 'The other company’s report, email, or our defect notes.')}
      {slotCard('AFTER', 'After photos', 'Photos taken after the repair.')}
      {slotCard('REQUEST', 'Request', 'What we asked the other company or our worker to do.')}

      <SectionCard title="Issue">
        <div className="flex flex-wrap gap-3">
          {showService && (
            <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" onClick={() => issue.mutate('service')}>
              {PATH_ISSUE[form.path_key] || 'Issue service report'}
            </button>
          )}
          {showReinspection && (
            <button type="button" className="rounded bg-gray-900 px-3 py-1.5 text-sm text-white" onClick={() => issue.mutate('reinspection')}>
              Re-inspection passed — issue report
            </button>
          )}
        </div>
        {message ? <p className="text-sm text-gray-800">{message}</p> : null}
        {issue.error ? <p className="text-sm text-gray-800">{issue.error.response?.data?.error || 'Could not file the report'}</p> : null}
      </SectionCard>
    </div>
  );
}

export default RepairCase;
